import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Search, X } from 'lucide-react';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyCheckbox,
  TaskyInput,
  TaskyLabel,
  TaskySelect,
  TaskySpinner,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
  TaskyTextarea,
} from '../tasky';
import PartnerCommissionReport from './PartnerCommissionReport';
import PartnerReferralList from './PartnerReferralList';
import adminPartnerPortalService, {
  AllowedTrialDay,
  AttributedOrganization,
  ConversionBonusType,
  PartnerCreateRequest,
  PartnerStatus,
  PartnerUpdateRequest,
  PortalPartner,
} from '../../services/api/adminPartnerPortalService';

const TRIAL_DAY_OPTIONS: AllowedTrialDay[] = [7, 14, 30];

// 招待URLとして配布する延長トライアル日数。ポータル側 app.js の
// EXTENDED_INVITE_TRIAL_DAYS と揃える（30日を配るならこちらにも追加する）。
const EXTENDED_INVITE_TRIAL_DAYS: AllowedTrialDay[] = [14];

// パートナーに許可されたトライアル期間。未設定・空はポータル実装と同じく全期間許可扱い。
const allowedTrialDaysOf = (partner: PortalPartner): AllowedTrialDay[] =>
  partner.allowedTrialDays && partner.allowedTrialDays.length > 0
    ? partner.allowedTrialDays
    : [...TRIAL_DAY_OPTIONS];

// 招待URL。trialDays 付きは「その日数のトライアルで登録される」延長版。
// 実際に延長してよいかは登録時にサーバーがポータルへ照会して判定する。
const inviteUrlFor = (code: string, trialDays?: number): string => {
  const base = `${window.location.origin}/signup?partner_ref=${encodeURIComponent(code)}`;
  return trialDays ? `${base}&trial_days=${trialDays}` : base;
};

interface PartnerFormState {
  name: string;
  code: string;
  brand: string;
  defaultCommissionRate: string;
  defaultWholesaleRate: string;
  allowedTrialDays: AllowedTrialDay[];
  conversionBonusType: ConversionBonusType;
  conversionBonusCredits: string;
  incentiveNote: string;
  billingEmails: string;
  status: PartnerStatus;
}

const emptyForm: PartnerFormState = {
  name: '',
  code: '',
  brand: '',
  defaultCommissionRate: '',
  defaultWholesaleRate: '80',
  allowedTrialDays: [...TRIAL_DAY_OPTIONS],
  conversionBonusType: 'none',
  conversionBonusCredits: '',
  incentiveNote: '',
  billingEmails: '',
  status: 'active',
};

// 一覧表示用の実効ボーナスラベル（旧データは credits>0 を fixed とみなす後方互換込み）
const formatConversionBonus = (partner: PortalPartner): string => {
  const type: ConversionBonusType =
    partner.conversionBonusType ??
    (partner.conversionBonusCredits && partner.conversionBonusCredits > 0 ? 'fixed' : 'none');
  if (type === 'plan_month') return 'プラン1ヶ月分';
  if (type === 'fixed') {
    return partner.conversionBonusCredits != null ? `${partner.conversionBonusCredits.toLocaleString()}cr` : '固定';
  }
  return 'なし';
};

const extractErrorMessage = (err: any, fallback: string): string => {
  const envelope = err?.response?.data?.error;
  if (envelope?.details && typeof envelope.details === 'object') {
    const messages = Object.values(envelope.details).flat().filter(Boolean);
    if (messages.length > 0) {
      return messages.join(' / ');
    }
  }
  return envelope?.message || err?.response?.data?.message || fallback;
};

// エラーレスポンスのコード（organizations_attached / clients_exist 等）を取り出す。
const extractErrorCode = (err: any): string | undefined => err?.response?.data?.error?.code;

const formatDate = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('ja-JP');
};

const numberOrNull = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? null : parsed;
};

const parseBillingEmails = (value: string): string[] =>
  value
    .split(/[\n,]/)
    .map(email => email.trim())
    .filter(email => email.length > 0);

const formatRate = (value?: number | null): string => (value === null || value === undefined ? '—' : `${value}%`);

const PartnerPortalManagement: React.FC = () => {
  // --- パートナー企業マスタ ---
  const [partners, setPartners] = useState<PortalPartner[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<PortalPartner | null>(null);
  const [form, setForm] = useState<PartnerFormState>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  // 削除確認
  const [deletingPartner, setDeletingPartner] = useState<PortalPartner | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // 紐付け企業モーダルの対象パートナー
  const [attributionPartner, setAttributionPartner] = useState<PortalPartner | null>(null);
  // 招待URLをコピーしたボタン（`<partnerId>:<days>`）。一定時間だけ「コピー済み」を出す。
  const [copiedInviteKey, setCopiedInviteKey] = useState<string | null>(null);

  const copyInviteUrl = useCallback(async (key: string, url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedInviteKey(key);
      window.setTimeout(
        () => setCopiedInviteKey(current => (current === key ? null : current)),
        2000,
      );
    } catch (err) {
      console.error('Failed to copy invite URL:', err);
      setError('招待URLをコピーできませんでした。URLを手動で選択してコピーしてください');
    }
  }, []);

  const fetchPartners = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await adminPartnerPortalService.getPartners();
      setPartners(data);
    } catch (err: any) {
      console.error('Failed to fetch partners:', err);
      setError(extractErrorMessage(err, 'パートナー企業一覧の取得に失敗しました'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  const openCreateModal = () => {
    setEditingPartner(null);
    setForm(emptyForm);
    setError(null);
    setIsModalOpen(true);
  };

  const openEditModal = (partner: PortalPartner) => {
    setEditingPartner(partner);
    setForm({
      name: partner.name ?? '',
      code: partner.code ?? '',
      brand: partner.brand ?? '',
      defaultCommissionRate: partner.defaultCommissionRate?.toString() ?? '',
      defaultWholesaleRate: partner.defaultWholesaleRate?.toString() ?? '',
      allowedTrialDays:
        partner.allowedTrialDays && partner.allowedTrialDays.length > 0
          ? [...partner.allowedTrialDays]
          : [...TRIAL_DAY_OPTIONS],
      conversionBonusType:
        partner.conversionBonusType ??
        (partner.conversionBonusCredits && partner.conversionBonusCredits > 0 ? 'fixed' : 'none'),
      conversionBonusCredits: partner.conversionBonusCredits?.toString() ?? '',
      incentiveNote: partner.incentiveNote ?? '',
      billingEmails: (partner.billingEmails ?? []).join('\n'),
      status: partner.status ?? 'active',
    });
    setError(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingPartner(null);
  };

  const toggleTrialDay = (day: AllowedTrialDay, checked: boolean) => {
    setForm(prev => ({
      ...prev,
      allowedTrialDays: checked
        ? [...prev.allowedTrialDays, day].filter((v, i, arr) => arr.indexOf(v) === i).sort((a, b) => a - b)
        : prev.allowedTrialDays.filter(d => d !== day),
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setError('企業名を入力してください');
      return;
    }
    if (!form.code.trim()) {
      setError('紹介コードを入力してください');
      return;
    }
    if (form.conversionBonusType === 'fixed' && (numberOrNull(form.conversionBonusCredits) ?? 0) < 1) {
      setError('固定クレジットのボーナスには1以上の付与数を入力してください');
      return;
    }

    const basePayload: PartnerCreateRequest = {
      name: form.name.trim(),
      code: form.code.trim(),
      brand: form.brand.trim() || null,
      defaultCommissionRate: numberOrNull(form.defaultCommissionRate),
      defaultWholesaleRate: numberOrNull(form.defaultWholesaleRate),
      allowedTrialDays: form.allowedTrialDays.length > 0 ? form.allowedTrialDays : null,
      conversionBonusType: form.conversionBonusType,
      conversionBonusCredits: form.conversionBonusType === 'fixed' ? numberOrNull(form.conversionBonusCredits) : null,
      incentiveNote: form.incentiveNote.trim() || null,
      billingEmails: parseBillingEmails(form.billingEmails),
    };

    setIsSaving(true);
    setError(null);
    try {
      if (editingPartner) {
        // 紹介コードは招待URL帰属のキーのため変更不可（API側もprohibited）
        const { code: _unusedCode, ...editableFields } = basePayload;
        const updatePayload: PartnerUpdateRequest = { ...editableFields, status: form.status };
        await adminPartnerPortalService.updatePartner(editingPartner.id, updatePayload);
      } else {
        await adminPartnerPortalService.createPartner(basePayload);
      }
      closeModal();
      await fetchPartners();
    } catch (err: any) {
      console.error('Failed to save partner:', err);
      setError(extractErrorMessage(err, 'パートナー企業の保存に失敗しました'));
    } finally {
      setIsSaving(false);
    }
  };

  const openDeleteConfirm = (partner: PortalPartner) => {
    setDeletingPartner(partner);
    setDeleteError(null);
  };

  const closeDeleteConfirm = () => {
    if (isDeleting) return;
    setDeletingPartner(null);
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingPartner) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await adminPartnerPortalService.deletePartner(deletingPartner.id);
      setDeletingPartner(null);
      await fetchPartners();
    } catch (err: any) {
      console.error('Failed to delete partner:', err);
      const code = extractErrorCode(err);
      const count = err?.response?.data?.error?.details?.count;
      if (code === 'organizations_attached') {
        setDeleteError(`紐付き組織が${count ?? ''}件あります。先に「紐付け企業」から帰属を解除してください。`);
      } else if (code === 'clients_exist') {
        setDeleteError('ポータル側に顧客データがあるため削除できません。状態を「停止」にしてください。');
      } else {
        setDeleteError(extractErrorMessage(err, 'パートナー企業の削除に失敗しました'));
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* ===== パートナー経由の組織一覧 ===== */}
      <PartnerReferralList partners={partners} />

      {/* ===== 手数料売上（代理店ごと・顧客ごと × 月次） ===== */}
      <PartnerCommissionReport />

      {/* ===== パートナー企業マスタ ===== */}
      <section>
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-base font-semibold text-dark">パートナー企業マスタ</h2>
            <p className="mt-1 text-xs text-gray-500">
              登録するのは企業マスタのみです（個人アカウント連携は行いません）。マスタの正本はパートナーポータル側に保存されます。
            </p>
          </div>
          <TaskyButton size="sm" onClick={openCreateModal} disabled={isLoading}>
            <Plus />
            新規パートナー登録
          </TaskyButton>
        </div>

        {error && !isModalOpen && (
          <TaskyAlert tone="error" className="mb-4">
            <div className="flex items-center gap-2">
              <span>{error}</span>
              <button
                type="button"
                onClick={() => setError(null)}
                aria-label="閉じる"
                className="shrink-0 text-dark/50 hover:text-dark transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </TaskyAlert>
        )}

        {isLoading && partners.length === 0 ? (
          <div className="flex items-center justify-center gap-3 py-10 text-sm text-gray-500">
            <TaskySpinner size="md" />
            読み込み中...
          </div>
        ) : (
          <TaskyTable className="min-w-[960px]">
            <TaskyTableHead>
              <TaskyTableRow hoverable={false}>
                <TaskyTableHeaderCell>コード</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>企業名</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>取次率</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>卸掛率</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>トライアル許可期間</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>招待URL</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>有料転換ボーナス</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>特典</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>状態</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>操作</TaskyTableHeaderCell>
              </TaskyTableRow>
            </TaskyTableHead>
            <TaskyTableBody>
              {partners.length === 0 ? (
                <TaskyTableRow hoverable={false}>
                  <TaskyTableCell colSpan={10} className="py-8 text-center text-gray-500">
                    パートナー企業はまだ登録されていません
                  </TaskyTableCell>
                </TaskyTableRow>
              ) : (
                partners.map(partner => (
                  <TaskyTableRow key={partner.id}>
                    <TaskyTableCell>
                      <code className="font-mono text-xs bg-gray-100 text-gray-700 rounded px-1.5 py-0.5">
                        {partner.code}
                      </code>
                    </TaskyTableCell>
                    <TaskyTableCell>
                      <div className="text-sm font-medium text-dark">{partner.name}</div>
                      {partner.brand && <div className="text-xs text-gray-500">{partner.brand}</div>}
                    </TaskyTableCell>
                    <TaskyTableCell className="whitespace-nowrap text-gray-600">
                      {formatRate(partner.defaultCommissionRate)}
                    </TaskyTableCell>
                    <TaskyTableCell className="whitespace-nowrap text-gray-600">
                      {formatRate(partner.defaultWholesaleRate)}
                    </TaskyTableCell>
                    <TaskyTableCell className="whitespace-nowrap text-gray-600">
                      {partner.allowedTrialDays && partner.allowedTrialDays.length > 0
                        ? partner.allowedTrialDays.map(d => `${d}日`).join(' / ')
                        : '—'}
                    </TaskyTableCell>
                    <TaskyTableCell>
                      {/* 招待URLは7日版と延長版を別ボタンで配れるようにする。
                          延長版はそのパートナーに許可された日数だけ表示する。 */}
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        {[undefined, ...EXTENDED_INVITE_TRIAL_DAYS.filter(day => allowedTrialDaysOf(partner).includes(day))].map(
                          day => {
                            const key = `${partner.id}:${day ?? 7}`;
                            const url = inviteUrlFor(partner.code, day);
                            return (
                              <button
                                key={key}
                                type="button"
                                onClick={() => copyInviteUrl(key, url)}
                                title={url}
                                className="text-xs font-medium text-primary hover:underline"
                              >
                                {copiedInviteKey === key ? 'コピー済み' : `${day ?? 7}日URL`}
                              </button>
                            );
                          },
                        )}
                      </div>
                    </TaskyTableCell>
                    <TaskyTableCell className="whitespace-nowrap text-gray-600">
                      {formatConversionBonus(partner)}
                    </TaskyTableCell>
                    <TaskyTableCell className="max-w-[200px] truncate text-gray-500" title={partner.incentiveNote ?? ''}>
                      {partner.incentiveNote || '—'}
                    </TaskyTableCell>
                    <TaskyTableCell>
                      <TaskyBadge variant={partner.status === 'active' ? 'success' : 'secondary'} dot>
                        {partner.status === 'active' ? 'アクティブ' : '停止'}
                      </TaskyBadge>
                    </TaskyTableCell>
                    <TaskyTableCell>
                      <div className="flex items-center gap-3 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => openEditModal(partner)}
                          className="text-xs font-medium text-primary hover:underline"
                        >
                          編集
                        </button>
                        <button
                          type="button"
                          onClick={() => setAttributionPartner(partner)}
                          className="text-xs font-medium text-primary hover:underline"
                        >
                          紐付け企業
                        </button>
                        <button
                          type="button"
                          onClick={() => openDeleteConfirm(partner)}
                          className="text-xs font-medium text-red-600 hover:underline"
                        >
                          削除
                        </button>
                      </div>
                    </TaskyTableCell>
                  </TaskyTableRow>
                ))
              )}
            </TaskyTableBody>
          </TaskyTable>
        )}
      </section>

      {/* ===== 組織のパートナー帰属管理 ===== */}
      <OrganizationAttributionPanel partners={partners} />

      {/* ===== 登録・編集モーダル ===== */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={event => event.target === event.currentTarget && closeModal()}
        >
          <div className="bg-white rounded-tasky-lg w-full max-w-[760px] max-h-[90vh] overflow-auto shadow-card">
            <div className="flex items-center justify-between px-6 py-4 border-b border-warm-border">
              <h5 className="text-lg font-semibold text-dark">
                {editingPartner ? 'パートナー企業を編集' : 'パートナー企業を登録'}
              </h5>
              <button
                type="button"
                onClick={closeModal}
                aria-label="閉じる"
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-5">
              {error && (
                <TaskyAlert tone="error" className="mb-4">
                  {error}
                </TaskyAlert>
              )}

              <div className="mb-4 rounded-tasky-sm bg-warm-bg px-3 py-2 text-xs text-gray-500">
                登録するのは企業マスタのみです。個人アカウント（ログインユーザー）の連携は行いません。
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <TaskyLabel htmlFor="partner-name" required>
                    企業名
                  </TaskyLabel>
                  <TaskyInput
                    id="partner-name"
                    value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    maxLength={255}
                    placeholder="株式会社サンプル"
                  />
                </div>
                <div>
                  <TaskyLabel htmlFor="partner-code" required>
                    紹介コード
                  </TaskyLabel>
                  <TaskyInput
                    id="partner-code"
                    value={form.code}
                    onChange={e => setForm({ ...form, code: e.target.value })}
                    maxLength={64}
                    placeholder="acme-agency"
                    disabled={!!editingPartner}
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    {editingPartner
                      ? '紹介コードは招待URLの帰属に使用されるため変更できません'
                      : '半角英数字・ハイフン・アンダースコアのみ'}
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <TaskyLabel htmlFor="partner-brand">ブランド名</TaskyLabel>
                  <TaskyInput
                    id="partner-brand"
                    value={form.brand}
                    onChange={e => setForm({ ...form, brand: e.target.value })}
                    maxLength={255}
                    placeholder="任意"
                  />
                </div>
                <div>
                  <TaskyLabel htmlFor="partner-commission">取次手数料率（%）</TaskyLabel>
                  <TaskyInput
                    id="partner-commission"
                    type="number"
                    min={0}
                    max={100}
                    step="0.1"
                    value={form.defaultCommissionRate}
                    onChange={e => setForm({ ...form, defaultCommissionRate: e.target.value })}
                    placeholder="例: 20"
                  />
                </div>
                <div>
                  <TaskyLabel htmlFor="partner-wholesale">卸掛率（%）</TaskyLabel>
                  <TaskyInput
                    id="partner-wholesale"
                    type="number"
                    min={0}
                    max={100}
                    step="0.1"
                    value={form.defaultWholesaleRate}
                    onChange={e => setForm({ ...form, defaultWholesaleRate: e.target.value })}
                    placeholder="既定 80"
                  />
                </div>
                <div className="sm:col-span-2">
                  <TaskyLabel>トライアル許可期間</TaskyLabel>
                  <div className="flex flex-wrap items-center gap-4 pt-1">
                    {TRIAL_DAY_OPTIONS.map(day => (
                      <TaskyCheckbox
                        key={day}
                        label={`${day}日`}
                        checked={form.allowedTrialDays.includes(day)}
                        onChange={e => toggleTrialDay(day, e.target.checked)}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <TaskyLabel htmlFor="partner-bonus-type">有料転換ボーナス</TaskyLabel>
                  <TaskySelect
                    id="partner-bonus-type"
                    value={form.conversionBonusType}
                    onChange={e => setForm({ ...form, conversionBonusType: e.target.value as ConversionBonusType })}
                  >
                    <option value="none">なし</option>
                    <option value="plan_month">プラン1ヶ月分</option>
                    <option value="fixed">固定クレジット</option>
                  </TaskySelect>
                  {form.conversionBonusType === 'fixed' && (
                    <TaskyInput
                      id="partner-bonus"
                      className="mt-2"
                      type="number"
                      min={1}
                      step="1"
                      value={form.conversionBonusCredits}
                      onChange={e => setForm({ ...form, conversionBonusCredits: e.target.value })}
                      placeholder="例: 1000"
                    />
                  )}
                  <p className="mt-1 text-xs text-gray-400">有料転換後の同期時（毎朝6時／手動同期）に自動付与</p>
                </div>
                {editingPartner && (
                  <div>
                    <TaskyLabel htmlFor="partner-status">状態</TaskyLabel>
                    <TaskySelect
                      id="partner-status"
                      value={form.status}
                      onChange={e => setForm({ ...form, status: e.target.value as PartnerStatus })}
                    >
                      <option value="active">アクティブ</option>
                      <option value="suspended">停止</option>
                    </TaskySelect>
                  </div>
                )}
                <div className="sm:col-span-2">
                  <TaskyLabel htmlFor="partner-incentive">特典メモ</TaskyLabel>
                  <TaskyTextarea
                    id="partner-incentive"
                    rows={2}
                    value={form.incentiveNote}
                    onChange={e => setForm({ ...form, incentiveNote: e.target.value })}
                    maxLength={2000}
                    placeholder="特典・インセンティブに関する備考"
                  />
                </div>
                <div className="sm:col-span-2">
                  <TaskyLabel htmlFor="partner-emails">請求メール</TaskyLabel>
                  <TaskyTextarea
                    id="partner-emails"
                    rows={2}
                    value={form.billingEmails}
                    onChange={e => setForm({ ...form, billingEmails: e.target.value })}
                    placeholder="1行に1件（改行またはカンマ区切り）"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-5 mt-4 border-t border-warm-border">
                <TaskyButton type="button" variant="secondary" onClick={closeModal} disabled={isSaving}>
                  キャンセル
                </TaskyButton>
                <TaskyButton type="submit" disabled={isSaving}>
                  {isSaving ? '保存中...' : editingPartner ? '更新' : '登録'}
                </TaskyButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== 削除確認モーダル ===== */}
      {deletingPartner && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={event => event.target === event.currentTarget && closeDeleteConfirm()}
        >
          <div className="bg-white rounded-tasky-lg w-full max-w-[480px] shadow-card">
            <div className="flex items-center justify-between px-6 py-4 border-b border-warm-border">
              <h5 className="text-lg font-semibold text-dark">パートナー企業を削除</h5>
              <button
                type="button"
                onClick={closeDeleteConfirm}
                aria-label="閉じる"
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5">
              {deleteError && (
                <TaskyAlert tone="error" className="mb-4">
                  {deleteError}
                </TaskyAlert>
              )}
              <p className="text-sm text-dark">
                <span className="font-medium">{deletingPartner.name}</span>
                （<code className="font-mono text-xs bg-gray-100 text-gray-700 rounded px-1.5 py-0.5">{deletingPartner.code}</code>）
                を削除します。
              </p>
              <p className="mt-2 text-xs text-gray-500">
                この操作は取り消せません。紐付き組織やポータル側の顧客データがある場合は削除できません。
              </p>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-warm-border">
              <TaskyButton type="button" variant="secondary" onClick={closeDeleteConfirm} disabled={isDeleting}>
                キャンセル
              </TaskyButton>
              <TaskyButton type="button" variant="destructive" onClick={handleConfirmDelete} disabled={isDeleting}>
                {isDeleting ? '削除中...' : '削除する'}
              </TaskyButton>
            </div>
          </div>
        </div>
      )}

      {/* ===== 紐付け企業モーダル ===== */}
      {attributionPartner && (
        <PartnerOrganizationsModal
          partner={attributionPartner}
          partners={partners}
          onClose={() => setAttributionPartner(null)}
        />
      )}
    </div>
  );
};

// --- パートナー起点の紐付け企業モーダル ---
interface PartnerOrganizationsModalProps {
  partner: PortalPartner;
  partners: PortalPartner[];
  onClose: () => void;
}

// あるパートナーに帰属している組織の一覧・解除と、組織検索からの紐付け（付替え）を1画面で行う。
const PartnerOrganizationsModal: React.FC<PartnerOrganizationsModalProps> = ({ partner, partners, onClose }) => {
  const [attributed, setAttributed] = useState<AttributedOrganization[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [keyword, setKeyword] = useState('');
  const [searchResults, setSearchResults] = useState<AttributedOrganization[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);

  const partnerLabel = (code: string): string => {
    const found = partners.find(p => p.code === code);
    return found ? `${found.name}（${code}）` : code;
  };

  const loadAttributed = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const results = await adminPartnerPortalService.searchOrganizations({ partner_ref: partner.code });
      setAttributed(results);
    } catch (err: any) {
      console.error('Failed to load attributed organizations:', err);
      setError(extractErrorMessage(err, '帰属組織の取得に失敗しました'));
    } finally {
      setIsLoading(false);
    }
  }, [partner.code]);

  useEffect(() => {
    loadAttributed();
  }, [loadAttributed]);

  const runSearch = useCallback(async () => {
    if (!keyword.trim()) return;
    setIsSearching(true);
    setError(null);
    try {
      const results = await adminPartnerPortalService.searchOrganizations({ q: keyword.trim() });
      setSearchResults(results);
      setHasSearched(true);
    } catch (err: any) {
      console.error('Failed to search organizations:', err);
      setError(extractErrorMessage(err, '組織の検索に失敗しました'));
    } finally {
      setIsSearching(false);
    }
  }, [keyword]);

  // partnerRef=null で解除、partner.code で紐付け（付替え）。完了後は両リストへ反映する。
  const applyAttribution = async (org: AttributedOrganization, partnerRef: string | null) => {
    setSavingId(org.id);
    setError(null);
    try {
      await adminPartnerPortalService.updateOrganizationAttribution(org.id, partnerRef);
      setSearchResults(prev => prev.map(o => (o.id === org.id ? { ...o, partner_ref: partnerRef } : o)));
      await loadAttributed();
    } catch (err: any) {
      console.error('Failed to update attribution:', err);
      setError(extractErrorMessage(err, 'パートナー帰属の更新に失敗しました'));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={event => event.target === event.currentTarget && onClose()}
    >
      <div className="bg-white rounded-tasky-lg w-full max-w-[760px] max-h-[90vh] overflow-auto shadow-card">
        <div className="flex items-center justify-between px-6 py-4 border-b border-warm-border">
          <div>
            <h5 className="text-lg font-semibold text-dark">紐付け企業の管理</h5>
            <p className="mt-0.5 text-xs text-gray-500">
              {partner.name}（
              <code className="font-mono bg-gray-100 text-gray-700 rounded px-1 py-0.5">{partner.code}</code>）
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="閉じる"
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {error && (
            <TaskyAlert tone="error">
              {error}
            </TaskyAlert>
          )}

          {/* 帰属中の組織一覧 */}
          <section>
            <h6 className="mb-2 text-sm font-semibold text-dark">帰属中の組織</h6>
            {isLoading ? (
              <div className="flex items-center justify-center gap-3 py-6 text-sm text-gray-500">
                <TaskySpinner size="md" />
                読み込み中...
              </div>
            ) : attributed.length === 0 ? (
              <p className="py-4 text-center text-sm text-gray-500">帰属している組織はありません</p>
            ) : (
              <TaskyTable>
                <TaskyTableHead>
                  <TaskyTableRow hoverable={false}>
                    <TaskyTableHeaderCell>ID</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>組織名</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>作成日</TaskyTableHeaderCell>
                    <TaskyTableHeaderCell>操作</TaskyTableHeaderCell>
                  </TaskyTableRow>
                </TaskyTableHead>
                <TaskyTableBody>
                  {attributed.map(org => (
                    <TaskyTableRow key={org.id}>
                      <TaskyTableCell className="text-gray-400">{org.id}</TaskyTableCell>
                      <TaskyTableCell className="font-medium">{org.name}</TaskyTableCell>
                      <TaskyTableCell className="text-gray-500">{formatDate(org.created_at)}</TaskyTableCell>
                      <TaskyTableCell>
                        <TaskyButton
                          size="sm"
                          variant="secondary"
                          disabled={savingId === org.id}
                          onClick={() => applyAttribution(org, null)}
                        >
                          {savingId === org.id ? '解除中...' : '解除'}
                        </TaskyButton>
                      </TaskyTableCell>
                    </TaskyTableRow>
                  ))}
                </TaskyTableBody>
              </TaskyTable>
            )}
          </section>

          {/* 組織検索して紐付け */}
          <section>
            <h6 className="mb-2 text-sm font-semibold text-dark">組織を検索して紐付け</h6>
            <form
              onSubmit={e => {
                e.preventDefault();
                runSearch();
              }}
              className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center"
            >
              <TaskyInput
                value={keyword}
                onChange={e => setKeyword(e.target.value)}
                placeholder="組織名で検索"
                className="sm:w-80"
              />
              <TaskyButton type="submit" size="sm" variant="secondary" disabled={isSearching || !keyword.trim()}>
                <Search />
                {isSearching ? '検索中...' : '検索'}
              </TaskyButton>
            </form>

            {hasSearched &&
              (searchResults.length === 0 ? (
                <p className="py-4 text-center text-sm text-gray-500">該当する組織が見つかりませんでした</p>
              ) : (
                <TaskyTable>
                  <TaskyTableHead>
                    <TaskyTableRow hoverable={false}>
                      <TaskyTableHeaderCell>ID</TaskyTableHeaderCell>
                      <TaskyTableHeaderCell>組織名</TaskyTableHeaderCell>
                      <TaskyTableHeaderCell>現在の帰属</TaskyTableHeaderCell>
                      <TaskyTableHeaderCell>操作</TaskyTableHeaderCell>
                    </TaskyTableRow>
                  </TaskyTableHead>
                  <TaskyTableBody>
                    {searchResults.map(org => {
                      const isCurrent = org.partner_ref === partner.code;
                      const hasOther = !!org.partner_ref && !isCurrent;
                      return (
                        <TaskyTableRow key={org.id}>
                          <TaskyTableCell className="text-gray-400">{org.id}</TaskyTableCell>
                          <TaskyTableCell className="font-medium">{org.name}</TaskyTableCell>
                          <TaskyTableCell>
                            {org.partner_ref ? (
                              <code className="font-mono text-xs bg-gray-100 text-gray-700 rounded px-1.5 py-0.5">
                                {partnerLabel(org.partner_ref)}
                              </code>
                            ) : (
                              <span className="text-gray-400">未設定</span>
                            )}
                          </TaskyTableCell>
                          <TaskyTableCell>
                            {isCurrent ? (
                              <span className="text-xs text-gray-400">このパートナーに帰属中</span>
                            ) : (
                              <TaskyButton
                                size="sm"
                                disabled={savingId === org.id}
                                onClick={() => applyAttribution(org, partner.code)}
                              >
                                {savingId === org.id ? '保存中...' : hasOther ? '付替え' : '紐付け'}
                              </TaskyButton>
                            )}
                          </TaskyTableCell>
                        </TaskyTableRow>
                      );
                    })}
                  </TaskyTableBody>
                </TaskyTable>
              ))}
          </section>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-warm-border">
          <TaskyButton type="button" variant="secondary" onClick={onClose}>
            閉じる
          </TaskyButton>
        </div>
      </div>
    </div>
  );
};

// --- 組織のパートナー帰属管理パネル ---
interface OrganizationAttributionPanelProps {
  partners: PortalPartner[];
}

const OrganizationAttributionPanel: React.FC<OrganizationAttributionPanelProps> = ({ partners }) => {
  const [keyword, setKeyword] = useState('');
  const [organizations, setOrganizations] = useState<AttributedOrganization[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingRefById, setPendingRefById] = useState<Record<number, string>>({});
  const [savingId, setSavingId] = useState<number | null>(null);

  const runSearch = useCallback(async () => {
    if (!keyword.trim()) {
      return;
    }
    setIsSearching(true);
    setError(null);
    try {
      const results = await adminPartnerPortalService.searchOrganizations({ q: keyword.trim() });
      setOrganizations(results);
      setPendingRefById({});
      setHasSearched(true);
    } catch (err: any) {
      console.error('Failed to search organizations:', err);
      setError(extractErrorMessage(err, '組織の検索に失敗しました'));
    } finally {
      setIsSearching(false);
    }
  }, [keyword]);

  const handleAssign = async (org: AttributedOrganization, partnerRef: string | null) => {
    setSavingId(org.id);
    setError(null);
    try {
      await adminPartnerPortalService.updateOrganizationAttribution(org.id, partnerRef);
      setOrganizations(prev => prev.map(o => (o.id === org.id ? { ...o, partner_ref: partnerRef } : o)));
    } catch (err: any) {
      console.error('Failed to update attribution:', err);
      setError(extractErrorMessage(err, 'パートナー帰属の更新に失敗しました'));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <section>
      <div className="mb-4">
        <h2 className="text-base font-semibold text-dark">組織のパートナー帰属</h2>
        <p className="mt-1 text-xs text-gray-500">
          組織名で検索し、パートナーコードを割り当て・解除できます。
          <span className="font-medium text-dark">管理パネルでの変更が最優先で反映されます。</span>
        </p>
      </div>

      <form
        onSubmit={e => {
          e.preventDefault();
          runSearch();
        }}
        className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center"
      >
        <TaskyInput
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
          placeholder="組織名で検索"
          className="sm:w-80"
        />
        <TaskyButton type="submit" size="sm" variant="secondary" disabled={isSearching || !keyword.trim()}>
          <Search />
          {isSearching ? '検索中...' : '検索'}
        </TaskyButton>
      </form>

      {error && (
        <TaskyAlert tone="error" className="mb-4">
          {error}
        </TaskyAlert>
      )}

      {hasSearched &&
        (organizations.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">該当する組織が見つかりませんでした</p>
        ) : (
          <TaskyTable className="min-w-[820px]">
            <TaskyTableHead>
              <TaskyTableRow hoverable={false}>
                <TaskyTableHeaderCell>ID</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>組織名</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>リーダー</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>現在の帰属</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>帰属を変更</TaskyTableHeaderCell>
              </TaskyTableRow>
            </TaskyTableHead>
            <TaskyTableBody>
              {organizations.map(org => {
                const pending = pendingRefById[org.id] ?? org.partner_ref ?? '';
                const isDirty = pending !== (org.partner_ref ?? '');
                return (
                  <TaskyTableRow key={org.id}>
                    <TaskyTableCell className="text-gray-400">{org.id}</TaskyTableCell>
                    <TaskyTableCell className="font-medium">{org.name}</TaskyTableCell>
                    <TaskyTableCell className="text-gray-500">{org.leader_email || '—'}</TaskyTableCell>
                    <TaskyTableCell>
                      {org.partner_ref ? (
                        <code className="font-mono text-xs bg-gray-100 text-gray-700 rounded px-1.5 py-0.5">
                          {org.partner_ref}
                        </code>
                      ) : (
                        <span className="text-gray-400">未設定</span>
                      )}
                    </TaskyTableCell>
                    <TaskyTableCell>
                      <div className="flex items-center gap-2">
                        <TaskySelect
                          value={pending}
                          onChange={e => setPendingRefById(prev => ({ ...prev, [org.id]: e.target.value }))}
                          className="w-52"
                        >
                          <option value="">（帰属なし）</option>
                          {partners.map(partner => (
                            <option key={partner.id} value={partner.code}>
                              {partner.name}（{partner.code}）
                            </option>
                          ))}
                          {/* 一覧に無い既存コードも保持して表示 */}
                          {org.partner_ref && !partners.some(p => p.code === org.partner_ref) && (
                            <option value={org.partner_ref}>{org.partner_ref}（未登録コード）</option>
                          )}
                        </TaskySelect>
                        <TaskyButton
                          size="sm"
                          disabled={!isDirty || savingId === org.id}
                          onClick={() => handleAssign(org, pending === '' ? null : pending)}
                        >
                          {savingId === org.id ? '保存中...' : '反映'}
                        </TaskyButton>
                      </div>
                    </TaskyTableCell>
                  </TaskyTableRow>
                );
              })}
            </TaskyTableBody>
          </TaskyTable>
        ))}
    </section>
  );
};

export default PartnerPortalManagement;
